import { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useRouter } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
import { localStorageAvailable } from 'src/utils/storage-available';
import { useSnackbar } from 'src/components/snackbar';
import { getSites } from '../../../cms-api/user_acl';
import { getList, get, update } from '../../../cms-api/site';
// import { getPageList } from '../../../cms-api/page';
import { SiteContext } from './site-context';
import { getSiteConfig } from './site-config';
import { SplashScreen } from '../../loading-screen';
import { createSiteConfig, getSiteData } from '../../../utils/site';
import { getAiSaver, updateTemplateDoc } from '../../../cms-api/ai';
import { useAuthContext } from '../../../auth/hooks';

export function SiteProvider({children}) {

  const router = useRouter();

  const params = useParams();
  const { site_code } = params;

  const [sites, setSites] = useState([]);

  const [site, setSiteState] = useState(null);

  const [siteConfig, setSiteConfig] = useState(null);
  
  const [tempSiteConfig, setTempSiteConfig] = useState(null);
  
  const [tempConfigHistory, setTempConfigHistory] = useState([]); // 一時保存の履歴配列
  const [historyIndex, setHistoryIndex] = useState(-1); // 履歴内の現在位置
  
  const [hasTempChanges, setHasTempChanges] = useState(false);

  const [loading, setLoading] = useState(true);
  
  const { enqueueSnackbar } = useSnackbar();
  
  const { user } = useAuthContext();

  const updateSiteConfig = useCallback((newConfig) => {
    setSiteConfig(newConfig);
    
    if (site && newConfig) {
      const storageAvailable = localStorageAvailable();
      if (storageAvailable) {
        const timestamp = new Date().getTime();
        const data = {
          siteId: site.site_id,
          timestamp
        };
        localStorage.setItem('last_config_change_timestamp', timestamp.toString());
        localStorage.setItem('site_config_reload', JSON.stringify(data));
      }
    }
  }, [site]);

  useEffect(() => {
    if (!site) return undefined;

    const handleStorageChange = (e) => {
      if (e.key === 'site_config_reload' && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          if (data.siteId === site.site_id && data.timestamp !== localStorage.getItem('last_config_change_timestamp')) {
            localStorage.setItem('last_config_change_timestamp', data.timestamp.toString());
            window.location.reload();
          }
        } catch (error) {
          console.error('Error parsing site config reload data:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [site]);

  const setSite = useCallback(async (newSite) => {

    setLoading(true);

    const oldSiteId = localStorage.getItem('site_id');

    let isPush = false;
    if (!newSite) {
      isPush = true;
    } else if (!oldSiteId) {
      isPush = true;
    } else if (newSite.site_id !== oldSiteId) {
      isPush = true;
    }

    if (newSite) {
      localStorage.setItem('site_id', newSite.site_id);

      const { siteData, configData } = await getSiteData(newSite.site_id);

      setSiteState(siteData);
      setSiteConfig(configData);
      setLoading(false);

    } else {
      localStorage.removeItem('site_id');
      setSiteState(null);
      setSiteConfig({});
      setLoading(false);
    }

    if (isPush) {
      if (newSite) {
        router.push(paths.dashboard.site.root(newSite.code));
      } else {
        // router.push(paths.dashboard.root);
      }
    }

  }, [router]);

  const saveTempConfig = useCallback((newConfig) => {
    const newHistory = historyIndex >= 0 
      ? tempConfigHistory.slice(0, historyIndex + 1)
      : [];
    
    const currentPath = window.location.pathname;
    
    const historyItem = {
      config: newConfig,
      pagePath: currentPath
    };
    
    const updatedHistory = [...newHistory, historyItem];
    
    const MAX_HISTORY_SIZE = 10;
    if (updatedHistory.length > MAX_HISTORY_SIZE) {
      updatedHistory.shift(); // 最も古い履歴を削除
    }

    setTempConfigHistory(updatedHistory);
    setHistoryIndex(updatedHistory.length - 1);
    setTempSiteConfig(newConfig);
    setHasTempChanges(true);

    if (site) {
      const storageAvailable = localStorageAvailable();
      if (storageAvailable) {
        const timestamp = new Date().getTime();
        const tempData = {
          siteId: site.site_id,
          tempConfig: newConfig,
          tempHistory: updatedHistory,
          historyIndex: updatedHistory.length - 1,
          hasTempChanges: true,
          timestamp
        };
        localStorage.setItem('temp_site_config_sync', JSON.stringify(tempData));
        localStorage.setItem('last_config_change_timestamp', timestamp.toString());
        localStorage.setItem('site_config_reload', JSON.stringify({
          siteId: site.site_id,
          timestamp
        }));
      }
    }
    
    enqueueSnackbar('設定を一時保存しました。適用するには「適用」ボタンをクリックしてください。', { variant: 'success' });
  }, [enqueueSnackbar, tempConfigHistory, historyIndex, site]);

  const getTempConfig = useCallback(() => tempSiteConfig || siteConfig, [tempSiteConfig, siteConfig]);

  const applyConfig = useCallback(async () => {
    if (!site) {
      return;
    }

    const { siteData, configData } = await getSiteData(site.site_id);

    setSiteState(siteData);
    setSiteConfig(configData);
  }, [site, setSiteState, setSiteConfig]);

  const applyTempConfig = useCallback(async () => {
    if (!tempSiteConfig || !site) {
      return;
    }

    try {

      const json_data_str = JSON.stringify(tempSiteConfig)

      const { siteData } = await getSiteData(site.site_id);
      
      siteData.expand_value.text.private_sys_admin_config = json_data_str;
      
      const sendData = {
        name: siteData.name,
        domain: siteData.domains[0],
        expand_value: siteData.expand_value,
        site_id: site.site_id,
      };
      
      await update(sendData);

      try {
        const updatedVersion = siteData.expand_value.text.private_sys_admin_config_version;
        const saverData = await getAiSaver();
        await updateTemplateDoc({
          address: saverData.address,
          site_id: site.site_id,
          version: updatedVersion,
          cms_token: saverData.token,
        });
        console.log('Template doc update requested successfully');
      } catch (templateDocError) {
        console.error('Error requesting template doc update:', templateDocError);
      }
      
      updateSiteConfig(tempSiteConfig);
      setHasTempChanges(false);
      
      // setTempConfigHistory([]);
      // setHistoryIndex(-1);
      
      const storageAvailable = localStorageAvailable();
      if (storageAvailable) {
        const timestamp = new Date().getTime();
        const tempData = {
          siteId: site.site_id,
          tempConfig: tempSiteConfig,
          tempHistory: tempConfigHistory,
          historyIndex,
          hasTempChanges: false, // 適用後は変更なしの状態
          timestamp
        };
        localStorage.setItem('temp_site_config_sync', JSON.stringify(tempData));
        localStorage.setItem('last_config_change_timestamp', timestamp.toString());
        localStorage.setItem('site_config_reload', JSON.stringify({
          siteId: site.site_id,
          timestamp
        }));
      }

      applyConfig();
      
      enqueueSnackbar('設定を適用しました', { variant: 'success' });
    } catch (error) {
      console.error('Error applying temporary configuration:', error);
      enqueueSnackbar('設定の適用に失敗しました', { variant: 'error' });
    }
  }, [tempSiteConfig, site, updateSiteConfig, enqueueSnackbar, tempConfigHistory, historyIndex, applyConfig]);

  useEffect(() => {
    getSites().then((res) => {
      if (res.site_ids?.length > 0) {
        getList({
          site_ids: res.site_ids,
        }).then((resAll) => {
          setSites(resAll.site_list);

          if (!site) {
            if (site_code) {
              const getSite = resAll.site_list.find((fsite) => fsite.code === site_code);
              setSite(getSite);
            } else if (resAll.site_list.length > 0) {
              const defSiteId = localStorage.getItem('site_id')

              if (defSiteId) {
                const defSite = resAll.site_list.find((fsite) => fsite.site_id === defSiteId);
                if (defSite) {
                  // setSite(defSite);
                  router.push(paths.dashboard.site.root(defSite.code));
                } else {
                  // setSite(resAll.site_list[0])
                  router.push(paths.dashboard.site.root(resAll.site_list[0].code));
                }
              } else {
                router.push(paths.dashboard.site.root(resAll.site_list[0].code));
                // setSite(resAll.site_list[0])
              }

            } else if (resAll.site_list.length === 0) {
              setSite(null)
            }
          }
        });
      } else {
        setSite(null)
      }
    });
  }, [site, setSite, setSiteConfig, site_code, router])

  const getPreviousTempConfig = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      
      const historyItem = tempConfigHistory[newIndex];
      const config = historyItem.config || historyItem;
      
      setTempSiteConfig(config);
      
      if (historyItem.pagePath && historyItem.pagePath !== window.location.pathname) {
        router.push(historyItem.pagePath);
      }
      
      if (site) {
        const storageAvailable = localStorageAvailable();
        if (storageAvailable) {
          const timestamp = new Date().getTime();
          const tempData = {
            siteId: site.site_id,
            tempConfig: config,
            tempHistory: tempConfigHistory,
            historyIndex: newIndex,
            hasTempChanges: true,
            timestamp
          };
          localStorage.setItem('temp_site_config_sync', JSON.stringify(tempData));
          localStorage.setItem('last_config_change_timestamp', timestamp.toString());
          localStorage.setItem('site_config_reload', JSON.stringify({
            siteId: site.site_id,
            timestamp
          }));
        }
      }
      
      return config;
    }
    return tempSiteConfig;
  }, [historyIndex, tempConfigHistory, tempSiteConfig, site, router]);

  const getConfigEditData = useCallback(() => {
    if (!site) return null;

    if (hasTempChanges) {
      const tmpConfig = getTempConfig()

      if (tmpConfig) {
        return JSON.parse(JSON.stringify(tmpConfig))
      }
    }

    const { configData } = createSiteConfig(site);

    return JSON.parse(JSON.stringify(configData))
  }, [site, getTempConfig, hasTempChanges]);

  const getNextTempConfig = useCallback(() => {
    if (historyIndex < tempConfigHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      
      const historyItem = tempConfigHistory[newIndex];
      const config = historyItem.config || historyItem;
      
      setTempSiteConfig(config);
      
      if (historyItem.pagePath && historyItem.pagePath !== window.location.pathname) {
        router.push(historyItem.pagePath);
      }
      
      if (site) {
        const storageAvailable = localStorageAvailable();
        if (storageAvailable) {
          const timestamp = new Date().getTime();
          const tempData = {
            siteId: site.site_id,
            tempConfig: config,
            tempHistory: tempConfigHistory,
            historyIndex: newIndex,
            hasTempChanges: true,
            timestamp
          };
          localStorage.setItem('temp_site_config_sync', JSON.stringify(tempData));
          localStorage.setItem('last_config_change_timestamp', timestamp.toString());
          localStorage.setItem('site_config_reload', JSON.stringify({
            siteId: site.site_id,
            timestamp
          }));
        }
      }
      
      return config;
    }
    return tempSiteConfig;
  }, [historyIndex, tempConfigHistory, tempSiteConfig, site, router]);

  const hasPreviousTempConfig = useCallback(() => historyIndex > 0, [historyIndex]);

  const hasNextTempConfig = useCallback(() => historyIndex < tempConfigHistory.length - 1, [historyIndex, tempConfigHistory]);

  const clearTempConfig = useCallback(() => {
    setTempSiteConfig(siteConfig);
    setTempConfigHistory(siteConfig ? [{ config: siteConfig, pagePath: window.location.pathname }] : []);
    setHistoryIndex(siteConfig ? 0 : -1);
    setHasTempChanges(false);
    
    if (site) {
      const storageAvailable = localStorageAvailable();
      if (storageAvailable) {
        const timestamp = new Date().getTime();
        const tempData = {
          siteId: site.site_id,
          tempConfig: siteConfig,
          tempHistory: siteConfig ? [{ config: siteConfig, pagePath: window.location.pathname }] : [],
          historyIndex: siteConfig ? 0 : -1,
          hasTempChanges: false,
          timestamp
        };
        localStorage.setItem('temp_site_config_sync', JSON.stringify(tempData));
        localStorage.setItem('last_config_change_timestamp', timestamp.toString());
        localStorage.setItem('site_config_reload', JSON.stringify({
          siteId: site.site_id,
          timestamp
        }));
      }
    }
    
    enqueueSnackbar('一時保存の設定変更をクリアしました', { variant: 'success' });
  }, [siteConfig, site, enqueueSnackbar]);

  useEffect(() => {
    if (siteConfig) {
      const storageAvailable = localStorageAvailable();
      if (storageAvailable) {
        const tempDataStr = localStorage.getItem('temp_site_config_sync');
        if (tempDataStr) {
          try {
            const tempData = JSON.parse(tempDataStr);
            if (tempData.siteId === site?.site_id) {
              setTempSiteConfig(tempData.tempConfig);
              setTempConfigHistory(tempData.tempHistory);
              setHistoryIndex(tempData.historyIndex);
              setHasTempChanges(tempData.hasTempChanges);
              return; // 既存データがあれば初期化をスキップ
            }
          } catch (error) {
            console.error('Error parsing temp site config sync data:', error);
          }
        }
      }
      
      setTempSiteConfig(siteConfig);
      setTempConfigHistory(siteConfig ? [{ config: siteConfig, pagePath: window.location.pathname }] : []);
      setHistoryIndex(siteConfig ? 0 : -1);
    }
  }, [siteConfig, site]);

  const memoizedValue = useMemo(
    () => ({
      site,
      siteConfig,
      tempSiteConfig,
      tempConfigHistory,
      historyIndex,
      hasTempChanges,
      sites,
      loading,
      setSite,
      setSiteConfig: updateSiteConfig,
      saveTempConfig,
      applyTempConfig,
      getTempConfig,
      clearTempConfig,
      getPreviousTempConfig,
      getNextTempConfig,
      hasPreviousTempConfig,
      hasNextTempConfig,
      getConfigEditData,
      setSiteState,
      applyConfig,
    }),
    [site, sites, loading, setSite, siteConfig,
      tempSiteConfig, tempConfigHistory, historyIndex,
      hasTempChanges, updateSiteConfig, saveTempConfig,
      applyTempConfig, getTempConfig, clearTempConfig,
      getPreviousTempConfig, getNextTempConfig, hasPreviousTempConfig,
      hasNextTempConfig, getConfigEditData, setSiteState, applyConfig]
  );

  return <SiteContext.Provider value={memoizedValue}>{ loading ? <SplashScreen /> : children }</SiteContext.Provider>;
}

SiteProvider.propTypes = {
  children: PropTypes.node,
};
